import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import { Role } from 'dashboard/models/role';
import { User, UserResponseAttributes, UserStatus } from 'dashboard/models/user';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { TableCell, TableRow } from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import { isHovered } from 'design-system/lib/pseudoClass';
import IconUserProfilePicture from 'images/icons/user-profile-picture.svg';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

const PROFILE_PICTURE_WIDTH = 24;

interface RoleUsersListRowProps {
  currentUser: User;
  disableRemoveUser: boolean;
  roles: Role[];
  user: User;
  setUserToRemove: (user: User) => void;
}

const RoleUsersListRow = ({
  currentUser,
  disableRemoveUser,
  roles,
  user,
  setUserToRemove,
}: RoleUsersListRowProps) => {
  const canAssignRoleUsers = usePermission(Permission.RoleAssignUser);

  const {
    'email-address': emailAddress,
    'name-first': nameFirst,
    'name-last': nameLast,
    'profile-picture-url': profilePictureUrl,
  } = user.attributes as UserResponseAttributes;

  const existingRoleIds = user.relationships.roles.data.map((r) => r.id);
  const existingRoleNames = roles
    .filter((r) => existingRoleIds.includes(r.id))
    .map((r) => r.attributes.name);

  const displayName = [nameFirst, nameLast].filter(Boolean).join(' ');
  const status = user.attributes.status;

  return (
    <StyledTableRow data-test={`users-list__user-row--${user.id}`}>
      <StyledNameCell data-test={`users-list__user-row--name-display-${user.id}`}>
        {status === UserStatus.Pending ? (
          <Sticker>Pending</Sticker>
        ) : status === UserStatus.Deactivated ? (
          <Sticker variant='danger'>Deactivated</Sticker>
        ) : (
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          name({ displayName, profilePictureUrl })
        )}
      </StyledNameCell>

      <StyledEmailAddressCell data-test={`users-list__user-row--email-display-${user.id}`}>
        <Paragraph color='primary'>
          {user.id === currentUser.id ? `${emailAddress} (You)` : emailAddress}
        </Paragraph>
      </StyledEmailAddressCell>

      <StyledRoleCell data-test={`users-list__user-row--role-display-${user.id}`}>
        {existingRoleNames.sort().map((name) => (
          <>
            <Sticker
              id={`users-list__user-role--${user.id}-${name}`}
              data-test={`users-list__user-role--${user.id}-${name}`}
              variant='primary'
            >
              {name}
            </Sticker>{' '}
          </>
        ))}
      </StyledRoleCell>

      <StyledContextMenuTableCell>
        {canAssignRoleUsers && (
          <ContextMenu
            data-test={`users-list__user-more--${user.id}`}
            items={[
              {
                id: `role-users-list-row__delete-user--${user.id}`,
                onClick: () => setUserToRemove(user),
                hidden: disableRemoveUser || user.relationships.roles.data.length === 1,
                label: 'Remove from role',
              },
            ]}
            renderItem={(item) => (
              <ContextMenu.Item
                key={item.id}
                id={`users-list__user-${item.id}--${user.id}`}
                data-test={`users-list__user-${item.id}--${user.id}`}
                onClick={(e) => {
                  // Needed to avoid triggering row click below
                  e.stopPropagation();
                  e.preventDefault();
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  item.onClick();
                }}
                variant={item.variant}
              >
                <Paragraph color='primary'>{item.label}</Paragraph>
              </ContextMenu.Item>
            )}
          />
        )}
      </StyledContextMenuTableCell>
    </StyledTableRow>
  );
};

const name = ({
  displayName,
  profilePictureUrl,
}: {
  displayName: string;
  profilePictureUrl: string;
}) => {
  return (
    <>
      {profilePictureUrl && (
        <>
          <StyledProfilePictureImage
            height={PROFILE_PICTURE_WIDTH}
            width={PROFILE_PICTURE_WIDTH}
            src={profilePictureUrl}
          />
          <Spacer height={0} width={1} />
        </>
      )}
      {!profilePictureUrl && displayName.length > 0 && (
        <>
          <StyledIconUserProfilePicture
            height={PROFILE_PICTURE_WIDTH}
            width={PROFILE_PICTURE_WIDTH}
          />
          <Spacer height={0} width={1} />
        </>
      )}
      {displayName.length > 0 && (
        <StyledNameParagraph color='primary'>{displayName}</StyledNameParagraph>
      )}
    </>
  );
};

export default RoleUsersListRow;

const StyledEmailAddressCell = styled(TableCell)`
  width: 40%;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledRoleCell = styled(TableCell)`
  width: 25%;
`;

const StyledContextMenuTableCell = styled(TableCell)`
  padding: 0 12px 0 12px;
`;

const StyledTableRow = styled(TableRow)`
  &${isHovered} {
    background-color: ${({ theme }) => theme.color.background.primary};
  }
`;

const StyledProfilePictureImage = styled('img')`
  border-radius: ${BORDER_RADIUSES.rounded};
  object-fit: cover;
`;

const StyledIconUserProfilePicture = styled(IconUserProfilePicture)`
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const StyledNameCell = styled(TableCell)`
  padding: 10px 12px 10px 12px;
  display: flex;
  height: 45px;
`;

const StyledNameParagraph = styled(Paragraph)`
  margin-top: 2px;
`;
